import React, { useState, useEffect } from 'react';
import { 
  Coins, X, Check, Loader2, QrCode, Sparkles, CreditCard, 
  ArrowRight, ShieldCheck, Copy, ArrowLeft, CheckCircle2,
  Crown, Gem, Clock, Calendar, Gift, Lightbulb, Receipt,
  Tag, AlertCircle, PackageCheck
} from 'lucide-react';
import { useAuth } from '../AuthProvider';
import { getAuthHeaders, customFetch } from '../services/apiService';
import { TopUpPackage } from '../types';
import { useTopUpPackages, useConfirmTopUpPaymentMutation } from '../hooks/useQueries';
import { useQueryClient } from '@tanstack/react-query';

interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialTab?: 'XU' | 'VIP';
}

interface CheckoutOrderData {
  requestId: string;
  paymentCode: string;
  transferContent: string;
  qrUrl: string;
  expectedExpireAt?: string;
  bankInfo: {
    bankName: string;
    accountNo: string;
    accountName: string;
    amount: number;
    transferContent?: string;
  };
  package?: TopUpPackage;
  originalPrice: number;
  finalPrice: number;
  discountMsg?: string;
  promoCodeApplied?: string;
}

export const TopUpModal: React.FC<TopUpModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  initialTab = 'XU'
}) => {
  const { userProfile, refetchProfile, isMonetizationEnabled } = useAuth();
  const queryClient = useQueryClient();
  
  // Wizard Step: 1 = Chọn gói & Ưu đãi, 2 = Thanh toán & Xác nhận
  const [step, setStep] = useState<1 | 2>(1);

  // Tab State: 'XU' | 'VIP'
  const [activeTab, setActiveTab] = useState<'XU' | 'VIP'>(initialTab);

  // React Query for Packages (chỉ fetch khi Modal được mở)
  const { data: packages = [], isLoading: loadingPackages } = useTopUpPackages(isOpen);
  const [selectedPackage, setSelectedPackage] = useState<TopUpPackage | null>(null);
  
  // React Query Mutation for Confirm Payment
  const confirmPaymentMutation = useConfirmTopUpPaymentMutation();

  // Promo Code State
  const [promoCode, setPromoCode] = useState<string>('');
  const [promoCodeInput, setPromoCodeInput] = useState<string>('');
  const [promoDiscountMsg, setPromoDiscountMsg] = useState<string | null>(null);
  const [previewPackage, setPreviewPackage] = useState<TopUpPackage | null>(null);
  const [promoLoading, setPromoLoading] = useState<boolean>(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  // Checkout Data & Flow State
  const [isProcessingStep1, setIsProcessingStep1] = useState(false);
  const [checkoutData, setCheckoutData] = useState<CheckoutOrderData | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Format Date DD/MM/YYYY helper
  const formatDate = (isoString?: string) => {
    if (!isoString) return '';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return isoString;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return isoString;
    }
  };

  // Reset & Load active packages on modal open
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setCheckoutData(null);
      setSuccessMsg(null);
      setPromoCode('');
      setPromoCodeInput('');
      setPromoDiscountMsg(null);
      setPreviewPackage(null);
      setPromoError(null);
      return;
    }

    setStep(1);
    setActiveTab(initialTab);
    setPromoCode('');
    setPromoCodeInput('');
    setPromoDiscountMsg(null);
    setPreviewPackage(null);
    setPromoError(null);

    // Select default item for initialTab
    if (packages.length > 0) {
      const initialFiltered = initialTab === 'XU'
        ? packages.filter(p => !p.type || p.type === 'XU')
        : packages.filter(p => p.type === 'VIP' || p.type === 'SVIP');

      // Ưu tiên chọn gói còn hàng
      const availableInitial = initialFiltered.find(p => {
        const maxQ = typeof p.maxQuantity === 'number' ? p.maxQuantity : 0;
        const soldQ = typeof p.soldQuantity === 'number' ? p.soldQuantity : 0;
        return maxQ <= 0 || (maxQ - soldQ) > 0;
      });

      if (availableInitial) {
        setSelectedPackage(availableInitial);
      } else if (initialFiltered.length > 0) {
        setSelectedPackage(initialFiltered[0]);
      } else {
        setSelectedPackage(packages[0]);
      }
    }
  }, [isOpen, initialTab, packages]);

  if (!isOpen) return null;

  const currentCredits = typeof userProfile?.credits === 'number' ? userProfile.credits : 0;

  // Lọc danh sách gói theo Tab
  const xuPackages = packages.filter(pkg => !pkg.type || pkg.type === 'XU' || pkg.packageType === 'XU');
  const vipPackages = packages.filter(pkg => pkg.type === 'VIP' || pkg.type === 'SVIP' || pkg.packageType === 'VIP' || pkg.packageType === 'SVIP');
  const currentTabPackages = activeTab === 'XU' ? xuPackages : vipPackages;

  // Xử lý khi chuyển Tab
  const handleTabChange = (tab: 'XU' | 'VIP') => {
    setActiveTab(tab);
    const targetPackages = tab === 'XU' ? xuPackages : vipPackages;
    
    const isCurrentPkgInTarget = targetPackages.some(
      p => (p.id && (p.id === selectedPackage?.id || p.id === selectedPackage?.packageId)) ||
           (p.packageId && (p.packageId === selectedPackage?.id || p.packageId === selectedPackage?.packageId))
    );

    if (!isCurrentPkgInTarget && targetPackages.length > 0) {
      const availableTarget = targetPackages.find(p => {
        const maxQ = typeof p.maxQuantity === 'number' ? p.maxQuantity : 0;
        const soldQ = typeof p.soldQuantity === 'number' ? p.soldQuantity : 0;
        return maxQ <= 0 || (maxQ - soldQ) > 0;
      });
      setSelectedPackage(availableTarget || targetPackages[0]);
      setPromoCode('');
      setPromoDiscountMsg(null);
      setPreviewPackage(null);
      setPromoError(null);
    }
  };

  // Tính toán giá hiển thị ở Bước 1
  const currentActivePkg = previewPackage || selectedPackage;
  const pkgBasePrice = (!isMonetizationEnabled) ? 0 : (currentActivePkg?.price ?? 0);
  const pkgOriginalPrice = (!isMonetizationEnabled) ? 0 : (currentActivePkg?.originalPrice ?? selectedPackage?.price ?? pkgBasePrice);
  const effectiveDiscountMsg = previewPackage?.discountMsg || selectedPackage?.discountMsg || promoDiscountMsg;
  const estimatedFinalPrice = Math.max(0, pkgBasePrice);
  const isEstimatedDiscounted = isMonetizationEnabled && (pkgOriginalPrice > estimatedFinalPrice || Boolean(effectiveDiscountMsg));

  // Kiểm tra gói đang chọn có bị hết lượt (Stock Out) hay không
  const selectedMaxQty = typeof selectedPackage?.maxQuantity === 'number' ? selectedPackage.maxQuantity : 0;
  const selectedSoldQty = typeof selectedPackage?.soldQuantity === 'number' ? selectedPackage.soldQuantity : 0;
  const isSelectedOutOfStock = selectedMaxQty > 0 && (selectedMaxQty - selectedSoldQty) <= 0;

  // Xử lý Áp dụng mã giảm giá qua API POST /api/topup/preview (Chỉ dùng khi Bật thu phí)
  const handleApplyPromoCode = async () => {
    if (!isMonetizationEnabled) return;
    const cleanCode = promoCodeInput.trim().toUpperCase();
    if (!cleanCode) {
      setPromoError('Vui lòng nhập mã giảm giá.');
      return;
    }
    if (!selectedPackage) {
      setPromoError('Vui lòng chọn một gói trước khi áp dụng mã.');
      return;
    }

    setPromoLoading(true);
    setPromoError(null);
    setPromoDiscountMsg(null);

    try {
      const pkgId = selectedPackage.id || selectedPackage.packageId;
      const res = await customFetch('/api/topup/preview', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          packageId: pkgId,
          promoCode: cleanCode
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        const returnedPkg = data.paymentDetails?.package || data.package;
        if (returnedPkg) {
          setPreviewPackage(returnedPkg);
          setPromoCode(cleanCode);
          if (returnedPkg.discountMsg) {
            setPromoDiscountMsg(returnedPkg.discountMsg);
          } else {
            setPromoDiscountMsg(`Áp dụng thành công mã khuyến mãi [${cleanCode}]`);
          }
        }
      } else {
        setPromoError(data.message || 'Mã giảm giá không hợp lệ hoặc đã hết hạn.');
        setPromoCode('');
        setPromoDiscountMsg(null);
        setPreviewPackage(null);
      }
    } catch (err) {
      console.error('Lỗi khi kiểm tra mã khuyến mãi:', err);
      setPromoError('Đã xảy ra lỗi khi kiểm tra mã khuyến mãi. Vui lòng thử lại.');
      setPromoCode('');
      setPromoDiscountMsg(null);
      setPreviewPackage(null);
    } finally {
      setPromoLoading(false);
    }
  };

  // Xử lý Hủy mã giảm giá
  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoCodeInput('');
    setPromoDiscountMsg(null);
    setPreviewPackage(null);
    setPromoError(null);
  };

  // ACTION BƯỚC 1: Xử lý Đặt hàng hoặc Nhận Quà Miễn Phí trực tiếp
  const handleProceedToCheckout = async () => {
    if (!selectedPackage) return;
    if (isSelectedOutOfStock) {
      alert('Gói này đã hết số lượng lượt nhận quà.');
      return;
    }

    setIsProcessingStep1(true);
    setSuccessMsg(null);

    try {
      const pkgId = selectedPackage.id || selectedPackage.packageId;
      const res = await customFetch('/api/topup/request', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          packageId: pkgId,
          promoCode: isMonetizationEnabled ? (promoCode || (promoCodeInput.trim().toUpperCase() || undefined)) : undefined
        }),
      });

      const data = await res.json();

      if (res.ok && (data.success || data.request)) {
        const reqObj = data.request || {};
        const details = data.paymentDetails || {};
        const finalPkg = details.package || data.package || previewPackage || selectedPackage;
        const finalAmount = reqObj.amountMoney ?? reqObj.amountVnd ?? details.price ?? finalPkg.price ?? estimatedFinalPrice;
        const origAmount = details.originalPrice ?? finalPkg.originalPrice ?? pkgOriginalPrice;

        // TRƯỜNG HỢP 1: GÓI 0 VNĐ HOẶC TẮT THU PHÍ -> HOÀN TẤT NHẬN QUÀ NGAY, KHÔNG MỞ BƯỚC CHUYỂN KHOẢN
        if (!isMonetizationEnabled || finalAmount === 0 || data.isFreeGift || data.completed) {
          const successGiftMessage = data.message || `Chúc mừng! Bạn đã nhận thành công gói quà tặng "${finalPkg.name}".`;
          setSuccessMsg(successGiftMessage);
          
          await refetchProfile();
          queryClient.invalidateQueries({ queryKey: ['topupPackages'] });
          queryClient.invalidateQueries({ queryKey: ['walletTransactions'] });
          queryClient.invalidateQueries({ queryKey: ['myWalletRequests'] });

          if (onSuccess) onSuccess();
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('topup_success'));
            window.dispatchEvent(new CustomEvent('topup_history_refresh'));
          }

          setTimeout(() => {
            setSuccessMsg(null);
            setCheckoutData(null);
            setStep(1);
            onClose();
          }, 2400);
          return;
        }
        
        // TRƯỜNG HỢP 2: BẬT THU PHÍ & CÓ GIÁ TIỀN > 0 -> CHUYỂN SANG BƯỚC 2 THANH TOÁN VIETQR
        setCheckoutData({
          requestId: reqObj._id || reqObj.id || data.requestId || 'topup-order',
          paymentCode: reqObj.paymentCode || details.paymentCode || data.paymentCode || 'NGS123456',
          transferContent: reqObj.transferContent || details.transferContent || data.transferContent || 'NGS TOPUP',
          qrUrl: details.qrUrl || data.qrUrl || `https://img.vietqr.io/image/MB-0388888888-compact.png?amount=${finalAmount}`,
          expectedExpireAt: data.expectedExpireAt || details.expectedExpireAt || reqObj.expectedExpireAt,
          bankInfo: details.bankInfo || data.bankInfo || {
            bankName: "MBBank (Ngân hàng Quân Đội)",
            accountNo: "0388888888",
            accountName: "VO THI MINH HAN",
            amount: finalAmount
          },
          package: finalPkg,
          originalPrice: origAmount,
          finalPrice: finalAmount,
          discountMsg: details.discountMsg || finalPkg.discountMsg || promoDiscountMsg || undefined,
          promoCodeApplied: promoCode || undefined
        });

        // Chuyển sang Bước 2
        setStep(2);
      } else {
        alert(data.message || 'Không thể tạo đơn quà tặng/thanh toán. Vui lòng thử lại.');
      }
    } catch (err) {
      console.error('Lỗi khi tiến hành nhận gói/thanh toán:', err);
      alert('Đã xảy ra lỗi kết nối. Vui lòng kiểm tra lại.');
    } finally {
      setIsProcessingStep1(false);
    }
  };

  // ACTION BƯỚC 2: Gọi POST /api/topup/confirm-payment -> Hoàn tất & Đóng Modal
  const handleConfirmPaymentStep2 = async () => {
    if (!checkoutData || !checkoutData.requestId) {
      alert('Không tìm thấy mã đơn hàng. Vui lòng quay lại Bước 1 và thử lại.');
      return;
    }

    try {
      const data = await confirmPaymentMutation.mutateAsync(checkoutData.requestId);
      const defaultMsg = 'Đã xác nhận thanh toán! Hệ thống đang kiểm tra và kích hoạt tài khoản trong 1 - 3 phút.';
      const msg = data.message || defaultMsg;
      
      setSuccessMsg(msg);
      await refetchProfile();
      queryClient.invalidateQueries({ queryKey: ['topupPackages'] });
      queryClient.invalidateQueries({ queryKey: ['walletTransactions'] });
      queryClient.invalidateQueries({ queryKey: ['myWalletRequests'] });

      if (onSuccess) onSuccess();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('topup_success'));
        window.dispatchEvent(new CustomEvent('topup_history_refresh'));
      }

      setTimeout(() => {
        setSuccessMsg(null);
        setCheckoutData(null);
        setStep(1);
        onClose();
      }, 2200);
    } catch (err: any) {
      console.error('Lỗi khi xác nhận thanh toán:', err);
      alert(err?.message || 'Đã xảy ra lỗi kết nối khi gửi xác nhận. Vui lòng kiểm tra lại đường truyền.');
    }
  };

  // Copy text to clipboard
  const handleCopy = (text: string, fieldName: string) => {
    const cleanText = text.trim();
    navigator.clipboard.writeText(cleanText);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Package info helper for Step 1
  const isSelectedSvip = selectedPackage?.type === 'SVIP';
  const isSelectedVip = selectedPackage?.type === 'VIP';

  // Modal Title Logic
  const modalTitle = !isMonetizationEnabled
    ? 'Trung Tâm Quà Tặng / Nhận Xu Miễn Phí'
    : step === 1 
      ? 'Nâng Cấp Gói Dịch Vụ & Nạp Xu' 
      : 'Thanh Toán & Quét Mã VietQR';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/70 backdrop-blur-xs animate-fade-in overflow-y-auto">
      {/* Modal Container */}
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 relative overflow-hidden transition-all duration-300 max-w-4xl lg:max-w-5xl w-full min-h-[580px] max-h-[92vh] flex flex-col my-auto">
        
        {/* ===================== MODAL HEADER ===================== */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold shadow-2xs ${
              !isMonetizationEnabled
                ? 'bg-gradient-to-br from-emerald-500 via-[#236539] to-teal-700 text-white'
                : step === 2
                ? 'bg-[#236539] text-white'
                : activeTab === 'VIP'
                ? 'bg-gradient-to-br from-amber-400 via-rose-500 to-amber-600 text-white'
                : 'bg-[#224783] text-white'
            }`}>
              {!isMonetizationEnabled ? (
                <Gift className="w-5 h-5 text-white" />
              ) : step === 2 ? (
                <Receipt className="w-5 h-5" />
              ) : activeTab === 'VIP' ? (
                <Crown className="w-5 h-5 text-white" />
              ) : (
                <Coins className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-base sm:text-lg font-black text-slate-900 tracking-tight">
                  {modalTitle}
                </h2>
                {!isMonetizationEnabled && (
                  <span className="hidden sm:inline-flex items-center space-x-1 px-2 py-0.5 bg-emerald-100 text-[#236539] text-[10px] font-black rounded-full uppercase">
                    <Sparkles className="w-3 h-3 text-emerald-600" />
                    <span>Quà Tặng 100% Free</span>
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 flex items-center space-x-2">
                <span>Số dư hiện tại: <strong className="text-amber-700 font-bold">{currentCredits.toLocaleString('vi-VN')} Xu</strong></span>
                {userProfile?.vipTier === 1 && (
                  <span className="text-[10px] px-1.5 py-0.2 bg-amber-100 text-amber-900 rounded font-bold">VIP</span>
                )}
                {userProfile?.vipTier === 2 && (
                  <span className="text-[10px] px-1.5 py-0.2 bg-rose-100 text-rose-900 rounded font-bold">SVIP</span>
                )}
              </p>
            </div>
          </div>

          {/* Stepper Indicator (Chỉ hiển thị khi Bật thu phí) */}
          {isMonetizationEnabled ? (
            <div className="hidden sm:flex items-center space-x-2 bg-slate-100 p-1 rounded-xl text-xs font-bold">
              <div className={`px-3 py-1 rounded-lg transition-all flex items-center space-x-1.5 ${
                step === 1 ? 'bg-white text-[#224783] shadow-xs' : 'text-slate-500'
              }`}>
                <span className={`w-4 h-4 rounded-full text-[10px] flex items-center justify-center font-black ${
                  step === 1 ? 'bg-[#224783] text-white' : 'bg-slate-300 text-slate-700'
                }`}>1</span>
                <span>Chọn gói & Ưu đãi</span>
              </div>
              <div className={`px-3 py-1 rounded-lg transition-all flex items-center space-x-1.5 ${
                step === 2 ? 'bg-[#236539] text-white shadow-xs' : 'text-slate-500'
              }`}>
                <span className={`w-4 h-4 rounded-full text-[10px] flex items-center justify-center font-black ${
                  step === 2 ? 'bg-white text-[#236539]' : 'bg-slate-300 text-slate-700'
                }`}>2</span>
                <span>Thanh toán VietQR</span>
              </div>
            </div>
          ) : (
            <div className="hidden sm:flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200/80 rounded-xl text-xs font-black">
              <Gift className="w-4 h-4 text-emerald-600" />
              <span>Chương trình Tri Ân Nhà Giáo Số</span>
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ===================== MODAL BODY ===================== */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 flex flex-col justify-between">
          {successMsg ? (
            /* Thông báo thành công */
            <div className="my-auto py-12 px-6 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-3xl text-center space-y-3 animate-fade-in max-w-lg mx-auto w-full">
              <div className="w-16 h-16 bg-emerald-100 text-[#236539] rounded-full flex items-center justify-center mx-auto shadow-xs">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-black text-emerald-950">
                {!isMonetizationEnabled ? 'Nhận Quà Tặng Thành Công!' : 'Giao Dịch Đã Được Tiếp Nhận!'}
              </h3>
              <p className="text-xs sm:text-sm font-semibold text-emerald-800 leading-relaxed">
                {successMsg}
              </p>
              <span className="text-[11px] text-emerald-600 font-bold block pt-2">
                Cửa sổ sẽ tự động đóng sau giây lát...
              </span>
            </div>
          ) : step === 1 ? (
            /* ============================================================ */
            /* BƯỚC 1: DANH SÁCH GÓI | CHI TIẾT GÓI & NHẬN QUÀ / THANH TOÁN */
            /* ============================================================ */
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 items-start h-full animate-fade-in">
              
              {/* ----------------- CỘT TRÁI (7/12): DANH SÁCH CÁC THẺ GÓI NẠP / GÓI QUÀ ----------------- */}
              <div className="lg:col-span-7 space-y-3.5">
                {/* 1. Tabs Chọn Gói: Gói Xu vs Đặc quyền VIP */}
                <div className="grid grid-cols-2 gap-1.5 p-1 bg-slate-100 rounded-2xl">
                  <button
                    type="button"
                    onClick={() => handleTabChange('XU')}
                    className={`py-2 px-3 rounded-xl text-xs font-extrabold flex items-center justify-center space-x-1.5 transition-all cursor-pointer ${
                      activeTab === 'XU'
                        ? 'bg-amber-500 text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                    }`}
                  >
                    <Coins className={`w-4 h-4 ${activeTab === 'XU' ? 'text-white' : 'text-amber-600'}`} />
                    <span>{!isMonetizationEnabled ? 'Gói Quà Tặng Xu' : 'Gói Nạp Xu'}</span>
                    {xuPackages.length > 0 && (
                      <span className={`ml-1 text-[10px] px-1.5 py-0.2 rounded-full ${
                        activeTab === 'XU' ? 'bg-amber-600/60 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {xuPackages.length}
                      </span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTabChange('VIP')}
                    className={`py-2 px-3 rounded-xl text-xs font-extrabold flex items-center justify-center space-x-1.5 transition-all cursor-pointer ${
                      activeTab === 'VIP'
                        ? 'bg-gradient-to-r from-[#224783] via-rose-600 to-amber-500 text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                    }`}
                  >
                    <Crown className={`w-4 h-4 ${activeTab === 'VIP' ? 'text-amber-300' : 'text-rose-500'}`} />
                    <span>{!isMonetizationEnabled ? 'Quà Tặng VIP / SVIP' : 'Nâng Cấp VIP / SVIP'}</span>
                    {vipPackages.length > 0 && (
                      <span className={`ml-1 text-[10px] px-1.5 py-0.2 rounded-full ${
                        activeTab === 'VIP' ? 'bg-black/20 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {vipPackages.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* 2. Danh Sách Gói Nạp */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                      {!isMonetizationEnabled 
                        ? (activeTab === 'XU' ? 'Chọn gói Xu quà tặng' : 'Chọn gói đặc quyền VIP quà tặng')
                        : (activeTab === 'XU' ? 'Chọn gói Xu mong muốn' : 'Chọn gói thành viên đặc quyền')}
                    </span>
                    {!isMonetizationEnabled && (
                      <span className="text-[11px] font-bold text-emerald-600 flex items-center space-x-1">
                        <Sparkles className="w-3 h-3" />
                        <span>Miễn phí 100%</span>
                      </span>
                    )}
                  </div>

                  {loadingPackages ? (
                    <div className="py-16 text-center text-slate-400">
                      <Loader2 className="w-7 h-7 animate-spin mx-auto mb-2 text-amber-500" />
                      <span className="text-xs">Đang tải danh sách gói...</span>
                    </div>
                  ) : currentTabPackages.length === 0 ? (
                    <div className="py-10 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-slate-200">
                      Chưa có gói quà tặng nào khả dụng trong mục này.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-4 max-h-[360px] overflow-y-auto pt-3.5 pb-2 px-1.5">
                      {currentTabPackages.map((pkg, index) => {
                        const isSelected = (selectedPackage?.id && selectedPackage?.id === pkg.id) || 
                                           (selectedPackage?.packageId && selectedPackage?.packageId === pkg.id);
                        const isVip = pkg.type === 'VIP' || pkg.packageType === 'VIP';
                        const isSvip = pkg.type === 'SVIP' || pkg.packageType === 'SVIP';

                        // Xử lý tồn kho (Stock limit)
                        const maxQty = typeof pkg.maxQuantity === 'number' ? pkg.maxQuantity : 0;
                        const soldQty = typeof pkg.soldQuantity === 'number' ? pkg.soldQuantity : 0;
                        const hasLimit = maxQty > 0;
                        const remainingStock = Math.max(0, maxQty - soldQty);
                        const isOutOfStock = hasLimit && remainingStock <= 0;

                        let cardStyle = 'border-slate-200 hover:border-slate-300 bg-white';
                        if (isOutOfStock) {
                          cardStyle = 'border-slate-200 bg-slate-100/80 opacity-60 cursor-not-allowed select-none';
                        } else if (isSelected) {
                          if (isSvip) {
                            cardStyle = 'border-rose-500 bg-gradient-to-br from-rose-50 via-amber-50/60 to-white shadow-sm ring-2 ring-rose-400/40 scale-[1.01]';
                          } else if (isVip) {
                            cardStyle = 'border-[#224783] bg-gradient-to-br from-blue-50/70 via-indigo-50/40 to-white shadow-sm ring-2 ring-[#224783]/30 scale-[1.01]';
                          } else {
                            cardStyle = 'border-[#236539] bg-emerald-50/40 shadow-xs ring-2 ring-[#236539]/30 scale-[1.01]';
                          }
                        } else {
                          if (isSvip) {
                            cardStyle = 'border-rose-200/80 hover:border-rose-400 bg-rose-50/20';
                          } else if (isVip) {
                            cardStyle = 'border-blue-200/80 hover:border-blue-400 bg-blue-50/20';
                          }
                        }

                        return (
                          <div
                            key={pkg.id || pkg.packageId || pkg._id || index}
                            onClick={() => {
                              if (isOutOfStock) return;
                              setSelectedPackage(pkg);
                              setPromoCode('');
                              setPromoDiscountMsg(null);
                              setPreviewPackage(null);
                              setPromoError(null);
                            }}
                            className={`p-3 rounded-2xl border-2 transition-all relative flex flex-col justify-between ${
                              isOutOfStock ? 'cursor-not-allowed' : 'cursor-pointer'
                            } ${cardStyle}`}
                          >
                            {/* Badges */}
                            {isOutOfStock ? (
                              <span className="absolute -top-2.5 right-3 z-10 px-2.5 py-0.5 bg-rose-600 text-white text-[9px] font-black rounded-full uppercase shadow-xs ring-2 ring-white whitespace-nowrap">
                                Hết lượt
                              </span>
                            ) : pkg.badgeText ? (
                              <span className="absolute -top-2.5 right-3 z-10 px-2.5 py-0.5 bg-gradient-to-r from-rose-600 to-amber-500 text-white text-[9px] font-black rounded-full uppercase shadow-xs ring-2 ring-white whitespace-nowrap">
                                {pkg.badgeText}
                              </span>
                            ) : isSvip ? (
                              <span className="absolute -top-2.5 right-3 z-10 px-2.5 py-0.5 bg-gradient-to-r from-rose-600 to-amber-500 text-white text-[9px] font-black rounded-full uppercase shadow-xs ring-2 ring-white whitespace-nowrap">
                                SVIP TRỌN ĐỜI
                              </span>
                            ) : isVip ? (
                              <span className="absolute -top-2.5 right-3 z-10 px-2.5 py-0.5 bg-gradient-to-r from-[#224783] to-blue-500 text-white text-[9px] font-black rounded-full uppercase shadow-xs ring-2 ring-white whitespace-nowrap">
                                VIP
                              </span>
                            ) : null}

                            <div>
                              <div className="font-black text-xs text-slate-900 flex items-center space-x-1.5 mb-1">
                                {isSvip ? (
                                  <Gem className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                                ) : isVip ? (
                                  <Crown className="w-3.5 h-3.5 text-[#224783] shrink-0" />
                                ) : (
                                  <Coins className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                )}
                                <span className="truncate">{pkg.name}</span>
                              </div>

                              {/* Duration / Credits */}
                              {(isVip || isSvip) ? (
                                <div className="space-y-0.5 my-1">
                                  <div className="flex items-center space-x-1 text-[11px] font-bold text-slate-700">
                                    <Clock className="w-3 h-3 text-slate-400" />
                                    <span>Hạn dùng: <strong>{pkg.durationDays || (isSvip ? 365 : 30)} ngày</strong></span>
                                  </div>
                                  {(pkg.credits ?? 0) > 0 && (
                                    <div className="text-[11px] font-extrabold text-amber-700">
                                      +{(pkg.credits ?? 0).toLocaleString('vi-VN')} Xu tặng kèm
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-base font-black text-amber-800 my-0.5 flex items-center space-x-1">
                                  <Coins className="w-4 h-4 text-amber-600" />
                                  <span>{(pkg.credits ?? 0).toLocaleString('vi-VN')} Xu</span>
                                </div>
                              )}

                              {/* Hiển thị giới hạn số lượng lượt nhận */}
                              {hasLimit && (
                                <div className="mt-1 pt-1 border-t border-slate-100 flex items-center justify-between text-[10px]">
                                  <span className="text-slate-400 font-semibold flex items-center space-x-1">
                                    <PackageCheck className="w-3 h-3 text-slate-400" />
                                    <span>Số lượng phát:</span>
                                  </span>
                                  {isOutOfStock ? (
                                    <span className="font-bold text-rose-600 bg-rose-50 px-1.5 py-0.2 rounded">
                                      Hết lượt
                                    </span>
                                  ) : (
                                    <span className="font-extrabold text-amber-700 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200/60">
                                      Còn lại: {remainingStock} lượt
                                    </span>
                                  )}
                                </div>
                              )}

                              {pkg.description && (
                                <p className="text-[10px] text-slate-500 mt-1 line-clamp-2 leading-relaxed">
                                  {pkg.description}
                                </p>
                              )}
                            </div>

                            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                              <span className="text-xs font-black text-slate-900">
                                {!isMonetizationEnabled || (pkg?.price ?? 0) === 0 ? (
                                  <span className="text-emerald-700 font-black">MIỄN PHÍ</span>
                                ) : (
                                  `${(pkg?.price ?? 0).toLocaleString('vi-VN')} đ`
                                )}
                              </span>
                              {isSelected && !isOutOfStock && (
                                <span className="w-5 h-5 rounded-full bg-[#236539] text-white flex items-center justify-center">
                                  <Check className="w-3 h-3" />
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* ----------------- CỘT PHẢI (5/12): CHI TIẾT ĐƠN HÀNG / QUÀ TẶNG & NÚT ACTION ----------------- */}
              <div className="lg:col-span-5 bg-slate-50/90 rounded-2xl p-4 sm:p-5 border border-slate-200 space-y-4 shadow-2xs flex flex-col justify-between">
                <div className="space-y-3.5">
                  <div className="flex items-center space-x-2 pb-2.5 border-b border-slate-200">
                    {!isMonetizationEnabled ? (
                      <Gift className="w-4 h-4 text-[#236539]" />
                    ) : (
                      <Receipt className="w-4 h-4 text-[#224783]" />
                    )}
                    <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">
                      {!isMonetizationEnabled ? 'Chi Tiết Gói Quà Tặng' : 'Chi Tiết Đơn Hàng'}
                    </h3>
                  </div>

                  {/* Thông tin gói đang chọn */}
                  <div className="bg-white p-3 rounded-xl border border-slate-200/90 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-slate-400 uppercase">
                        {!isMonetizationEnabled ? 'Quà tặng đã chọn' : 'Gói đã chọn'}
                      </span>
                      {isSelectedSvip ? (
                        <span className="px-2 py-0.5 bg-gradient-to-r from-rose-600 to-amber-500 text-white font-black text-[10px] rounded-lg">SVIP</span>
                      ) : isSelectedVip ? (
                        <span className="px-2 py-0.5 bg-[#224783] text-white font-black text-[10px] rounded-lg">VIP</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-900 font-bold text-[10px] rounded-lg">XU</span>
                      )}
                    </div>
                    
                    <div className="font-black text-slate-900 text-sm">
                      {selectedPackage ? selectedPackage.name : 'Vui lòng chọn 1 gói'}
                    </div>

                    {/* Báo số lượng còn lại nếu có */}
                    {selectedMaxQty > 0 && (
                      <div className="text-[11px] font-semibold flex items-center justify-between pt-1 border-t border-slate-100">
                        <span className="text-slate-400">Giới hạn số lượng:</span>
                        {isSelectedOutOfStock ? (
                          <span className="font-bold text-rose-600">Đã hết lượt nhận</span>
                        ) : (
                          <span className="font-bold text-amber-700">Còn lại: {selectedMaxQty - selectedSoldQty} lượt</span>
                        )}
                      </div>
                    )}

                    {(isSelectedVip || isSelectedSvip) && (
                      <div className="text-xs font-semibold text-slate-600 flex items-center space-x-1.5">
                        <Clock className="w-3.5 h-3.5 text-slate-400" />
                        <span>Thời hạn: <strong>{selectedPackage?.durationDays || (isSelectedSvip ? 365 : 30)} ngày</strong></span>
                      </div>
                    )}

                    {(selectedPackage?.credits ?? 0) > 0 && (
                      <div className="text-xs font-bold text-amber-800 flex items-center space-x-1.5">
                        <Coins className="w-3.5 h-3.5 text-amber-600" />
                        <span>Số Xu nhận được: <strong>+{(selectedPackage?.credits ?? 0).toLocaleString('vi-VN')} Xu</strong></span>
                      </div>
                    )}
                  </div>

                  {/* Giao diện nhập "Mã giảm giá / Khuyến mãi" (Chỉ hiển thị khi Bật thu phí) */}
                  {isMonetizationEnabled && (
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center space-x-1 uppercase tracking-wider">
                        <Tag className="w-3 h-3 text-[#224783]" />
                        <span>Mã giảm giá / Khuyến mãi</span>
                      </label>

                      <div className="flex items-center space-x-1.5">
                        <input
                          type="text"
                          value={promoCodeInput}
                          onChange={(e) => {
                            setPromoCodeInput(e.target.value.toUpperCase());
                            if (promoError) setPromoError(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleApplyPromoCode();
                            }
                          }}
                          placeholder="Nhập mã giảm giá..."
                          className="flex-1 px-3 py-2 text-xs bg-white border border-slate-200 rounded-xl font-mono font-bold uppercase focus:outline-none focus:ring-2 focus:ring-[#224783]/30"
                        />
                        <button
                          type="button"
                          disabled={promoLoading || !promoCodeInput.trim()}
                          onClick={handleApplyPromoCode}
                          className="px-4 py-2 bg-[#224783] hover:bg-[#1a3664] text-white text-xs font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center space-x-1 cursor-pointer shrink-0"
                        >
                          {promoLoading ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              <span>Đang kiểm tra...</span>
                            </>
                          ) : (
                            <span>Áp dụng</span>
                          )}
                        </button>
                      </div>

                      {/* Dòng tin nhắn màu xanh lá cây khi mã hợp lệ và có discountMsg */}
                      {promoDiscountMsg && (
                        <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between text-xs animate-fade-in">
                          <div className="flex items-center space-x-1.5 text-emerald-800 font-semibold text-[11px]">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                            <span>{promoDiscountMsg}</span>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemovePromo}
                            className="text-[10px] font-bold text-rose-600 hover:text-rose-800 underline ml-2 cursor-pointer shrink-0"
                          >
                            Hủy bỏ
                          </button>
                        </div>
                      )}

                      {/* Thông báo lỗi màu đỏ nếu API báo lỗi */}
                      {promoError && (
                        <p className="text-[11px] text-rose-600 font-semibold flex items-center space-x-1 animate-fade-in">
                          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                          <span>{promoError}</span>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Khu vực Giá & Giá tạm tính */}
                  <div className="pt-2 border-t border-slate-200/80 space-y-2">
                    {isMonetizationEnabled ? (
                      <>
                        {/* Giá gốc (gạch bỏ nếu có giảm giá) */}
                        {isEstimatedDiscounted ? (
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-bold text-slate-400">Giá niêm yết:</span>
                            <span className="line-through text-slate-400 font-semibold">
                              {pkgOriginalPrice.toLocaleString('vi-VN')} đ
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-bold text-slate-500">Giá gói:</span>
                            <span className="font-bold text-slate-700">
                              {pkgOriginalPrice.toLocaleString('vi-VN')} đ
                            </span>
                          </div>
                        )}

                        {/* Box thông báo ưu đãi hệ thống khác */}
                        {selectedPackage?.discountMsg && selectedPackage.discountMsg !== promoDiscountMsg && (
                          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-[11px] text-amber-900 font-medium flex items-start space-x-1.5 animate-fade-in">
                            <Lightbulb className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                            <span>{selectedPackage.discountMsg}</span>
                          </div>
                        )}

                        {/* Giá tạm tính / Tổng thanh toán */}
                        <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-2xs flex items-center justify-between">
                          <div>
                            <span className="text-[10px] font-black text-slate-400 uppercase block tracking-wider">
                              Tổng thanh toán
                            </span>
                            <span className="text-[11px] text-slate-500">
                              (Đã bao gồm khuyến mãi)
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-xl font-black text-[#236539] tracking-tight block">
                              {estimatedFinalPrice === 0 ? 'MIỄN PHÍ' : `${estimatedFinalPrice.toLocaleString('vi-VN')} đ`}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      /* Chế độ Quà tặng miễn phí */
                      <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 shadow-2xs flex items-center justify-between">
                        <div>
                          <span className="text-[10px] font-black text-emerald-700 uppercase block tracking-wider">
                            Chi phí nhận quà
                          </span>
                          <span className="text-[11px] text-emerald-600 font-medium">
                            (Tri ân cộng đồng Nhà Giáo Số)
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-xl font-black text-[#236539] tracking-tight block">
                            0 VNĐ
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Nút bấm lớn Action */}
                <div className="pt-2">
                  <button
                    type="button"
                    disabled={isProcessingStep1 || !selectedPackage || isSelectedOutOfStock}
                    onClick={handleProceedToCheckout}
                    className={`w-full py-3.5 px-5 font-black text-sm rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-center space-x-2 disabled:opacity-50 active:scale-98 cursor-pointer ${
                      !isMonetizationEnabled
                        ? 'bg-[#236539] hover:bg-[#1a4e2b] text-white'
                        : 'bg-[#224783] hover:bg-[#1a3664] text-white'
                    }`}
                  >
                    {isProcessingStep1 ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>{!isMonetizationEnabled ? 'Đang kích hoạt quà tặng...' : 'Đang tạo thông tin thanh toán...'}</span>
                      </>
                    ) : isSelectedOutOfStock ? (
                      <>
                        <AlertCircle className="w-4 h-4" />
                        <span>Gói này đã hết lượt nhận</span>
                      </>
                    ) : !isMonetizationEnabled ? (
                      <>
                        <Gift className="w-4 h-4" />
                        <span>Nhận Quà Ngay</span>
                      </>
                    ) : (
                      <>
                        <span>Tiếp tục thanh toán</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                  <p className="text-[10px] text-center text-slate-400 mt-2">
                    {!isMonetizationEnabled
                      ? 'Nhấn để nhận quà tặng ngay lập tức vào tài khoản của bạn'
                      : 'Nhấn để nhận mã chuyển khoản VietQR tự động'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* ============================================================ */
            /* BƯỚC 2: THANH TOÁN & CHUYỂN KHOẢN (CHỈ DÙNG KHI BẬT THU PHÍ) */
            /* ============================================================ */
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 items-start h-full animate-fade-in">
              
              {/* ----------------- CỘT TRÁI (6/12): BOX THÔNG TIN NGÂN HÀNG & MÃ QR CODE ----------------- */}
              {isMonetizationEnabled && (
                <div className="lg:col-span-6 bg-slate-50/90 rounded-2xl p-4 sm:p-5 border border-slate-200 space-y-3.5 shadow-2xs">
                  <div className="flex items-center justify-between pb-2.5 border-b border-slate-200">
                    <div className="flex items-center space-x-2">
                      <CreditCard className="w-4 h-4 text-[#224783]" />
                      <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">
                        Thông Tin Chuyển Khoản VietQR
                      </h3>
                    </div>
                    <span className="text-[10px] font-black px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-md">
                      24/7 Tự Động
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3.5 items-center">
                    {/* Mã QR Code */}
                    <div className="sm:col-span-5 bg-white p-3 rounded-2xl border border-slate-200 flex flex-col items-center justify-center text-center shadow-2xs">
                      <img
                        src={checkoutData?.qrUrl}
                        alt="Mã QR VietQR"
                        className="w-32 h-32 sm:w-36 sm:h-36 object-contain rounded-lg"
                      />
                      <span className="text-[10px] text-slate-500 font-bold mt-1.5 flex items-center space-x-1">
                        <QrCode className="w-3 h-3 text-[#224783]" />
                        <span>Quét bằng App Ngân hàng</span>
                      </span>
                    </div>

                    {/* Chi tiết tài khoản ngân hàng */}
                    <div className="sm:col-span-7 space-y-2 text-xs">
                      {/* Ngân hàng */}
                      <div className="bg-white p-2 rounded-xl border border-slate-100">
                        <span className="text-[9px] text-slate-400 block uppercase font-bold tracking-wider">
                          Ngân hàng thụ hưởng
                        </span>
                        <span className="font-extrabold text-slate-900 text-xs">
                          {checkoutData?.bankInfo.bankName}
                        </span>
                      </div>

                      {/* Số tài khoản */}
                      <div className="bg-white p-2 rounded-xl border border-slate-100 relative">
                        <span className="text-[9px] text-slate-400 block uppercase font-bold tracking-wider">
                          Số tài khoản
                        </span>
                        <span className="font-mono font-black text-[#224783] text-sm tracking-wider">
                          {checkoutData?.bankInfo.accountNo}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopy(checkoutData?.bankInfo.accountNo || '', 'accNo')}
                          className="absolute top-2 right-2 px-2 py-0.5 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg border border-slate-200 transition-colors text-[9px] font-bold flex items-center space-x-1 cursor-pointer shadow-2xs"
                        >
                          {copiedField === 'accNo' ? (
                            <>
                              <Check className="w-2.5 h-2.5 text-emerald-600" />
                              <span className="text-emerald-700">Đã chép</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-2.5 h-2.5 text-slate-500" />
                              <span>Sao chép</span>
                            </>
                          )}
                        </button>
                      </div>

                      {/* Chủ tài khoản */}
                      <div className="bg-white p-2 rounded-xl border border-slate-100">
                        <span className="text-[9px] text-slate-400 block uppercase font-bold tracking-wider">
                          Chủ tài khoản
                        </span>
                        <span className="font-bold text-slate-900 text-xs">
                          {checkoutData?.bankInfo.accountName}
                        </span>
                      </div>

                      {/* Cú pháp chuyển khoản NỔI BẬT */}
                      <div className="bg-amber-50 p-2 rounded-xl border border-amber-200 relative">
                        <span className="text-[9px] text-amber-800 block uppercase font-black tracking-wider">
                          Cú pháp chuyển khoản <span className="text-rose-500">*</span>
                        </span>
                        <span className="font-mono font-black text-amber-950 text-xs block tracking-wide">
                          {checkoutData?.paymentCode}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopy(checkoutData?.paymentCode || '', 'code')}
                          className="absolute top-2 right-2 px-2 py-0.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors text-[9px] font-black flex items-center space-x-1 shadow-2xs cursor-pointer active:scale-95"
                        >
                          {copiedField === 'code' ? (
                            <>
                              <Check className="w-2.5 h-2.5" />
                              <span>Đã chép</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-2.5 h-2.5" />
                              <span>Sao chép</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ----------------- CỘT PHẢI (6/12): XÁC NHẬN ĐƠN HÀNG & NÚT SUBMIT ----------------- */}
              <div className={`${isMonetizationEnabled ? 'lg:col-span-6' : 'lg:col-span-12'} bg-white rounded-2xl p-4 sm:p-5 border border-slate-200 space-y-4 shadow-2xs flex flex-col justify-between`}>
                <div className="space-y-3.5">
                  <div className="flex items-center space-x-2 pb-2.5 border-b border-slate-100">
                    <Receipt className="w-4 h-4 text-[#236539]" />
                    <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">
                      Xác Nhận Đơn Hàng
                    </h3>
                  </div>

                  {/* Tóm tắt Đơn hàng */}
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Gói đã chọn:</span>
                      <span className="font-extrabold text-slate-900">
                        {checkoutData?.package?.name || selectedPackage?.name}
                      </span>
                    </div>

                    {(checkoutData?.package?.credits ?? 0) > 0 && (
                      <div className="flex items-center justify-between text-amber-800">
                        <span className="font-medium">Số Xu nhận được:</span>
                        <span className="font-black">+{(checkoutData?.package?.credits ?? 0).toLocaleString('vi-VN')} Xu</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                      <span className="font-bold text-slate-700">Giá trị thanh toán:</span>
                      <span className="font-black text-lg text-[#236539]">
                        {(checkoutData?.finalPrice ?? 0).toLocaleString('vi-VN')} đ
                      </span>
                    </div>
                  </div>

                  {/* Hiển thị Ngày hết hạn VIP mới dự kiến */}
                  {checkoutData?.expectedExpireAt && (
                    <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl text-xs space-y-1 animate-fade-in shadow-2xs">
                      <div className="flex items-center space-x-1.5 font-black text-[#224783]">
                        <Calendar className="w-4 h-4 text-[#224783]" />
                        <span>Thời gian hết hạn VIP mới dự kiến:</span>
                      </div>
                      <div className="text-sm font-black text-slate-900 pl-5">
                        {formatDate(checkoutData.expectedExpireAt)}
                      </div>
                    </div>
                  )}

                  {/* Hướng dẫn chuyển khoản */}
                  {isMonetizationEnabled && (
                    <div className="p-3 bg-amber-50/80 rounded-xl border border-amber-200/80 text-[11px] text-amber-950 leading-relaxed flex items-start space-x-2">
                      <Lightbulb className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                      <span>
                        Vui lòng ghi <strong>chính xác cú pháp chuyển khoản ở trên</strong> để hệ thống tự động kiểm tra và kích hoạt từ <strong>1 - 3 phút</strong>.
                      </span>
                    </div>
                  )}
                </div>

                {/* 2 Nút điều hướng */}
                <div className="flex items-center space-x-2.5 pt-2">
                  <button
                    type="button"
                    disabled={confirmPaymentMutation.isPending}
                    onClick={() => setStep(1)}
                    className="w-1/3 py-3 px-3 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-xl transition-all flex items-center justify-center space-x-1 cursor-pointer disabled:opacity-50"
                  >
                    <ArrowLeft className="w-3.5 h-3.5 text-slate-500" />
                    <span>Quay lại</span>
                  </button>

                  <button
                    type="button"
                    disabled={confirmPaymentMutation.isPending}
                    onClick={handleConfirmPaymentStep2}
                    className="w-2/3 py-3 px-4 bg-[#236539] hover:bg-[#1a4e2b] text-white font-black text-xs sm:text-sm rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center space-x-1.5 disabled:opacity-50 active:scale-98 cursor-pointer"
                  >
                    {confirmPaymentMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Đang gửi xác nhận...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Tôi đã chuyển khoản</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ===================== MODAL FOOTER ===================== */}
        <div className="px-5 py-2.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500 shrink-0">
          <div className="flex items-center space-x-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>
              {!isMonetizationEnabled 
                ? 'Chương trình Quà Tặng & Tri Ân Thành Viên Nhà Giáo Số' 
                : 'Thanh toán bảo mật chuẩn VietQR Napas 24/7'}
            </span>
          </div>
          <span className="text-slate-400 font-medium">Nhà Giáo Số Platform</span>
        </div>

      </div>
    </div>
  );
};

export default TopUpModal;
